import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  IconButton,
  InputAdornment,
  CircularProgress,
  Tabs,
  Tab,
  Select,
  MenuItem,
  Stack,
  Link as MuiLink
} from '@mui/material';
import { useSelector, useDispatch } from "react-redux";
import AlertDialog from '../components/AlertDialog';
import CountrySelect from '../components/CountrySelect';
import { FirebaseContext, api } from 'common';
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { Helmet } from 'react-helmet-async';
import { FONT_FAMILY } from "../common/sharedFunctions.js";
import EmailIcon from '@mui/icons-material/EmailOutlined';
import LockIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/VisibilityOutlined';
import VisibilityOff from '@mui/icons-material/VisibilityOffOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';

export default function LoginPage(props) {
  const { authRef, RecaptchaVerifier, signInWithPhoneNumber } = useContext(FirebaseContext);
  const { t, i18n } = useTranslation();
  const {
    googleLogin,
    clearLoginError,
    mobileSignIn,
    countries,
    sendResetMail,
    verifyEmailPassword,
    requestMobileOtp,
    verifyMobileOtp,
    mainSignUp,
    checkUserExists,
  } = api;
  const navigate = useNavigate();
  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settingsdata.settings);
  const dispatch = useDispatch();
  const [capatchaReady, setCapatchaReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [verifier, setVerifier] = useState(null);
  const [showSignUp, setShowSignUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [authMethod, setAuthMethod] = useState(0);
  const isRTL = i18n.dir();

  const [data, setData] = useState({
    email: '',
    confirmpassword: "",
    country: "234",
    contact: '',
    mobile: "",
    password: '',
    otp: '',
    verificationId: null,
    firstName: '',
    lastName: '',
    selectedcountry: null,
    usertype: 'customer',
    referralId: '',
    entryType: null
  });

  const [commonAlert, setCommonAlert] = useState({ open: false, msg: '' });

  useEffect(() => {
    if (settings && countries) {
      const defaultCountry = settings.country || 'Nigeria';
      for (let i = 0; i < countries.length; i++) {
        if (countries[i].label === defaultCountry) {
          setData(prev => ({
            ...prev,
            country: countries[i].phone || '234',
            selectedcountry: countries[i],
          }));
          break;
        }
      }
    }
  }, [settings, countries]);

  useEffect(() => {
    if (auth.profile && auth.profile.uid) {
      const role = auth.profile.usertype;
      if (role === 'admin' || role === 'fleetadmin') {
        navigate('/dashboard', { replace: true });
      } else if (role === 'driver' || role === 'customer') {
        navigate('/bookings', { replace: true });
      }
    }
    if (auth.error && auth.error.flag && auth.error.msg.message !== t('not_logged_in')) {
      if (auth.error.msg.message === t('require_approval')) {
        setCommonAlert({ open: true, msg: t('require_approval') });
      } else {
        setCommonAlert({ open: true, msg: auth.error.msg.message || t('login_error') });
      }
      setIsLoading(false);
    }
  }, [auth.profile, auth.error, navigate, t]);

  const handleGoogleLogin = (credentialResponse) => {
    if (credentialResponse && credentialResponse.credential) {
      dispatch(googleLogin(credentialResponse.credential, null));
    } else {
      setCommonAlert({ open: true, msg: t('login_error') });
      setIsLoading(false);
    }
  };

  const handleCommonAlertClose = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setCommonAlert({ open: false, msg: '' });
    if (auth.error?.flag) {
      dispatch(clearLoginError());
    }
  };

  const onInputChange = (event) => {
    setData({ ...data, [event.target.id || event.target.name]: event.target.value });
  };

  const handleGetOTP = async (e) => {
    if (e) e.preventDefault();
    setIsLoading(true);
    if (data.country || authMethod === 0) {
      if (data.contact) {
        if (isNaN(data.contact) || authMethod === 0) {
          // Email & Password login
          setData({ ...data, entryType: 'email' });
          const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          if (re.test(data.contact)) {
            await dispatch(verifyEmailPassword(data.contact, data.otp));
          } else {
            setCommonAlert({ open: true, msg: t('proper_email') });
            setIsLoading(false);
          }
        } else {
          // Phone OTP login
          setData({ ...data, entryType: 'mobile' });
          let formattedNum = data.contact.replace(/ /g, '');
          const phoneNumber = "+" + data.country + formattedNum;
          if (formattedNum.length > 6) {
            if (settings?.customMobileOTP) {
              dispatch(requestMobileOtp(phoneNumber));
              setData({ ...data, verificationId: true });
              setIsLoading(false);
            } else {
              if (!window.recaptchaVerifier || verifier === null) {
                window.recaptchaVerifier = await new RecaptchaVerifier(authRef(), 'sign-in-button', {
                  'size': 'invisible',
                  'callback': () => setCapatchaReady(true)
                });
              }
              const appVerifier = window.recaptchaVerifier;
              setVerifier(appVerifier);
              await signInWithPhoneNumber(authRef(), phoneNumber, appVerifier)
                .then(res => {
                  setData({ ...data, verificationId: res.verificationId });
                  setIsLoading(false);
                  if (window.recaptchaVerifier) window.recaptchaVerifier.clear();
                })
                .catch(error => {
                  setCommonAlert({ open: true, msg: error.code + ": " + error.message });
                  setIsLoading(false);
                });
            }
          } else {
            setCommonAlert({ open: true, msg: t('mobile_no_blank_error') });
            setIsLoading(false);
          }
        }
      } else {
        setCommonAlert({ open: true, msg: t('contact_input_error') });
        setIsLoading(false);
      }
    } else {
      setCommonAlert({ open: true, msg: t('country_blank_error') });
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    if (e) e.preventDefault();
    setIsLoading(true);
    if (data.otp && data.otp.length === 6) {
      if (settings?.customMobileOTP) {
        let formattedNum = data.contact.replace(/ /g, '');
        const phoneNumber = "+" + data.country + formattedNum;
        dispatch(verifyMobileOtp(phoneNumber, data.otp));
      } else {
        await dispatch(mobileSignIn(data.verificationId, data.otp));
      }
      setIsLoading(false);
    } else {
      setCommonAlert({ open: true, msg: t('otp_validate_error') });
      setIsLoading(false);
    }
  };

  const forgotPassword = async () => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (re.test(data.contact || data.email)) {
      await dispatch(sendResetMail(data.contact || data.email));
      setCommonAlert({ open: true, msg: t('email_send') });
    } else {
      setCommonAlert({ open: true, msg: t('proper_email') });
    }
  };

  const handleSignUp = (e) => {
    e.preventDefault();
    setIsLoading(true);
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (re.test(data.email)) {
      if (data.firstName && data.lastName && data.password) {
        if (data.password !== data.confirmpassword) {
          setCommonAlert({ open: true, msg: t('confirm_password_not_match_err') });
          setIsLoading(false);
          return;
        }
        const userData = { ...data };
        delete userData.confirmpassword;
        checkUserExists(userData).then((res) => {
          if (res.users && res.users.length > 0) {
            setCommonAlert({ open: true, msg: t('user_exists') });
            setIsLoading(false);
          } else {
            mainSignUp(userData).then((res) => {
              if (res.uid) {
                setCommonAlert({ open: true, msg: 'Account registered successfully! You may now sign in.' });
                setShowSignUp(false);
              } else {
                setCommonAlert({ open: true, msg: t('reg_error') });
              }
              setIsLoading(false);
            });
          }
        });
      } else {
        setCommonAlert({ open: true, msg: t('no_details_error') });
        setIsLoading(false);
      }
    } else {
      setCommonAlert({ open: true, msg: t('proper_email') });
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', backgroundColor: '#F8FAFC', direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      <Helmet>
        <title>{showSignUp ? 'Create Account | HIILLA Web Console' : 'Sign In | HIILLA Web Console'}</title>
        <meta name="description" content="Log into the HIILLA Web Console to manage fleet dispatch, live orders, and driver operations." />
      </Helmet>
      <div id="sign-in-button" />

      <Grid container sx={{ minHeight: '100vh' }}>
        {/* ── Left Form Panel ── */}
        <Grid
          item
          xs={12}
          md={6}
          lg={5}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            p: { xs: 3, sm: 5, lg: 7 },
            backgroundColor: '#FFFFFF'
          }}
        >
          {/* Brand Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                component="img"
                src={require("../assets/img/logo192x192.png")}
                alt="Logo"
                sx={{ width: 42, height: 42, borderRadius: '10px', boxShadow: '0 4px 14px rgba(229, 35, 42, 0.35)' }}
              />
              <Typography variant="h6" sx={{ fontFamily: FONT_FAMILY, fontWeight: 800, color: '#0F172A', letterSpacing: '0.5px' }}>
                {settings?.appName || 'HIILLA'}
              </Typography>
            </Box>

            <MuiLink
              href="https://hiilla.com"
              target="_blank"
              rel="noopener"
              sx={{
                fontFamily: FONT_FAMILY,
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#64748B',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                textDecoration: 'none',
                '&:hover': { color: '#E5232A' }
              }}
            >
              hiilla.com <OpenInNewIcon sx={{ fontSize: 14 }} />
            </MuiLink>
          </Box>

          {/* Form Content Area */}
          <Box sx={{ maxWidth: 440, width: '100%', mx: 'auto', my: 'auto', py: 3 }}>
            {!showSignUp ? (
              /* ── SIGN IN FORM ── */
              <Box>
                <Typography variant="h4" sx={{ fontFamily: FONT_FAMILY, fontWeight: 800, color: '#0F172A', fontSize: { xs: '1.6rem', sm: '1.9rem' }, letterSpacing: '-0.02em', mb: 1 }}>
                  Sign in to HIILLA
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: FONT_FAMILY, color: '#64748B', fontSize: '0.88rem', mb: 3.5 }}>
                  Access dispatch, fleet operations, or your rider account.
                </Typography>

                {/* Method Switch Tabs */}
                <Box sx={{ borderBottom: '1px solid #E2E8F0', mb: 3 }}>
                  <Tabs
                    value={authMethod}
                    onChange={(e, val) => setAuthMethod(val)}
                    sx={{
                      '& .MuiTabs-indicator': { backgroundColor: '#E5232A', height: 3 },
                      '& .MuiTab-root': {
                        fontFamily: FONT_FAMILY,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.86rem',
                        color: '#64748B',
                        '&.Mui-selected': { color: '#E5232A', fontWeight: 700 }
                      }
                    }}
                  >
                    <Tab label="Email & Password" />
                    <Tab label="Phone Number (OTP)" />
                  </Tabs>
                </Box>

                {/* Email Tab */}
                {authMethod === 0 && (
                  <form onSubmit={handleGetOTP}>
                    <Stack spacing={2.2}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        id="contact"
                        placeholder="admin@hiilla.com"
                        label="Email Address"
                        value={data.contact}
                        onChange={onInputChange}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon sx={{ color: '#94A3B8', fontSize: 20 }} />
                            </InputAdornment>
                          ),
                          sx: { fontFamily: FONT_FAMILY, borderRadius: '10px', backgroundColor: '#F8FAFC' }
                        }}
                      />

                      <TextField
                        fullWidth
                        variant="outlined"
                        id="otp"
                        label="Password"
                        placeholder="••••••••"
                        type={showPassword ? 'text' : 'password'}
                        value={data.otp}
                        onChange={onInputChange}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockIcon sx={{ color: '#94A3B8', fontSize: 20 }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                                {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                              </IconButton>
                            </InputAdornment>
                          ),
                          sx: { fontFamily: FONT_FAMILY, borderRadius: '10px', backgroundColor: '#F8FAFC' }
                        }}
                      />

                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <MuiLink
                          component="button"
                          type="button"
                          onClick={forgotPassword}
                          sx={{ fontFamily: FONT_FAMILY, fontSize: '0.82rem', color: '#E5232A', fontWeight: 600, textDecoration: 'none' }}
                        >
                          Forgot Password?
                        </MuiLink>
                      </Box>

                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={isLoading}
                        endIcon={!isLoading && <ArrowForwardIcon fontSize="small" />}
                        sx={{
                          fontFamily: FONT_FAMILY,
                          textTransform: 'none',
                          fontWeight: 700,
                          fontSize: '0.95rem',
                          height: 48,
                          borderRadius: '10px',
                          backgroundColor: '#E5232A',
                          boxShadow: '0 4px 14px rgba(229, 35, 42, 0.35)',
                          '&:hover': { backgroundColor: '#C81E24' }
                        }}
                      >
                        {isLoading ? <CircularProgress size={22} color="inherit" /> : 'Sign In'}
                      </Button>
                    </Stack>
                  </form>
                )}

                {/* Phone OTP Tab */}
                {authMethod === 1 && (
                  <Box>
                    {!data.verificationId ? (
                      <form onSubmit={handleGetOTP}>
                        <Stack spacing={2.2}>
                          {settings?.AllowCountrySelection && countries && (
                            <CountrySelect
                              countries={countries}
                              label={t('select_country')}
                              value={data.selectedcountry}
                              onChange={(e, val) => {
                                if (val) setData({ ...data, country: val.phone, selectedcountry: val });
                              }}
                            />
                          )}

                          <TextField
                            fullWidth
                            variant="outlined"
                            id="contact"
                            label="Mobile Number"
                            placeholder="8012345678"
                            value={data.contact}
                            onChange={onInputChange}
                            required
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, pr: 0.5 }}>
                                    <Typography sx={{ fontSize: '1.1rem', lineHeight: 1 }}>
                                      {data.country === '234' ? '🇳🇬' : '🌐'}
                                    </Typography>
                                    <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#0F172A', fontSize: '0.92rem' }}>
                                      +{data.country || '234'}
                                    </Typography>
                                  </Box>
                                </InputAdornment>
                              ),
                              sx: { fontFamily: FONT_FAMILY, borderRadius: '10px', backgroundColor: '#F8FAFC' }
                            }}
                          />

                          <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={isLoading}
                            sx={{
                              fontFamily: FONT_FAMILY,
                              textTransform: 'none',
                              fontWeight: 700,
                              fontSize: '0.95rem',
                              height: 48,
                              borderRadius: '10px',
                              backgroundColor: '#E5232A',
                              boxShadow: '0 4px 14px rgba(229, 35, 42, 0.35)',
                              '&:hover': { backgroundColor: '#C81E24' }
                            }}
                          >
                            {isLoading ? <CircularProgress size={22} color="inherit" /> : 'Send Verification OTP'}
                          </Button>
                        </Stack>
                      </form>
                    ) : (
                      <form onSubmit={handleVerifyOTP}>
                        <Stack spacing={2.2}>
                          <Typography variant="body2" sx={{ fontFamily: FONT_FAMILY, color: '#475569', fontSize: '0.85rem' }}>
                            Enter the 6-digit verification code sent to <b>+{data.country}{data.contact}</b>
                          </Typography>

                          <TextField
                            fullWidth
                            variant="outlined"
                            id="otp"
                            label="Verification Code"
                            placeholder="123456"
                            value={data.otp}
                            onChange={onInputChange}
                            required
                            autoFocus
                            InputProps={{
                              sx: { fontFamily: FONT_FAMILY, borderRadius: '10px', backgroundColor: '#F8FAFC', fontSize: '1.1rem', letterSpacing: '4px', textAlign: 'center' }
                            }}
                          />

                          <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={isLoading}
                            sx={{
                              fontFamily: FONT_FAMILY,
                              textTransform: 'none',
                              fontWeight: 700,
                              fontSize: '0.95rem',
                              height: 48,
                              borderRadius: '10px',
                              backgroundColor: '#E5232A',
                              boxShadow: '0 4px 14px rgba(229, 35, 42, 0.35)',
                              '&:hover': { backgroundColor: '#C81E24' }
                            }}
                          >
                            {isLoading ? <CircularProgress size={22} color="inherit" /> : 'Verify Code & Sign In'}
                          </Button>
                        </Stack>
                      </form>
                    )}
                  </Box>
                )}

                {/* Social Login Divider */}
                <Box sx={{ my: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Divider sx={{ flex: 1 }} />
                  <Typography variant="caption" sx={{ color: '#94A3B8', fontFamily: FONT_FAMILY, fontWeight: 600 }}>
                    OR
                  </Typography>
                  <Divider sx={{ flex: 1 }} />
                </Box>

                {/* Google Sign In */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                  <GoogleLogin
                    onSuccess={handleGoogleLogin}
                    onError={() => setCommonAlert({ open: true, msg: t('login_error') })}
                    theme="outline"
                    size="large"
                    text="continue_with"
                    shape="rectangular"
                  />
                </Box>

                {/* Toggle to Sign Up */}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ fontFamily: FONT_FAMILY, color: '#64748B', fontSize: '0.85rem' }}>
                    Don't have an account?{' '}
                    <MuiLink
                      component="button"
                      type="button"
                      onClick={() => setShowSignUp(true)}
                      sx={{ fontFamily: FONT_FAMILY, color: '#E5232A', fontWeight: 700, textDecoration: 'none' }}
                    >
                      Create an account
                    </MuiLink>
                  </Typography>
                </Box>
              </Box>
            ) : (
              /* ── SIGN UP FORM ── */
              <Box>
                <Typography variant="h4" sx={{ fontFamily: FONT_FAMILY, fontWeight: 800, color: '#0F172A', fontSize: { xs: '1.5rem', sm: '1.8rem' }, letterSpacing: '-0.02em', mb: 1 }}>
                  Create HIILLA Account
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: FONT_FAMILY, color: '#64748B', fontSize: '0.88rem', mb: 3 }}>
                  Sign up as a customer, driver, or fleet operator.
                </Typography>

                <form onSubmit={handleSignUp}>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        id="firstName"
                        name="firstName"
                        label="First Name"
                        value={data.firstName}
                        onChange={onInputChange}
                        required
                        InputProps={{ sx: { fontFamily: FONT_FAMILY, borderRadius: '10px', backgroundColor: '#F8FAFC' } }}
                      />
                      <TextField
                        fullWidth
                        variant="outlined"
                        id="lastName"
                        name="lastName"
                        label="Last Name"
                        value={data.lastName}
                        onChange={onInputChange}
                        required
                        InputProps={{ sx: { fontFamily: FONT_FAMILY, borderRadius: '10px', backgroundColor: '#F8FAFC' } }}
                      />
                    </Box>

                    <TextField
                      fullWidth
                      variant="outlined"
                      id="email"
                      name="email"
                      label="Email Address"
                      type="email"
                      value={data.email}
                      onChange={onInputChange}
                      required
                      InputProps={{ sx: { fontFamily: FONT_FAMILY, borderRadius: '10px', backgroundColor: '#F8FAFC' } }}
                    />

                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        id="password"
                        name="password"
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        value={data.password}
                        onChange={onInputChange}
                        required
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                                {showPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                              </IconButton>
                            </InputAdornment>
                          ),
                          sx: { fontFamily: FONT_FAMILY, borderRadius: '10px', backgroundColor: '#F8FAFC' }
                        }}
                      />
                      <TextField
                        fullWidth
                        variant="outlined"
                        id="confirmpassword"
                        name="confirmpassword"
                        label="Confirm"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={data.confirmpassword}
                        onChange={onInputChange}
                        required
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" size="small">
                                {showConfirmPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                              </IconButton>
                            </InputAdornment>
                          ),
                          sx: { fontFamily: FONT_FAMILY, borderRadius: '10px', backgroundColor: '#F8FAFC' }
                        }}
                      />
                    </Box>

                    <Select
                      fullWidth
                      value={data.usertype}
                      onChange={(e) => setData({ ...data, usertype: e.target.value })}
                      sx={{ fontFamily: FONT_FAMILY, borderRadius: '10px', backgroundColor: '#F8FAFC' }}
                    >
                      <MenuItem value="customer">Customer / Sender Account</MenuItem>
                      <MenuItem value="driver">Rider / Delivery Partner</MenuItem>
                    </Select>

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={isLoading}
                      sx={{
                        fontFamily: FONT_FAMILY,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        height: 48,
                        borderRadius: '10px',
                        backgroundColor: '#E5232A',
                        boxShadow: '0 4px 14px rgba(229, 35, 42, 0.35)',
                        '&:hover': { backgroundColor: '#C81E24' }
                      }}
                    >
                      {isLoading ? <CircularProgress size={22} color="inherit" /> : 'Complete Registration'}
                    </Button>

                    <Box sx={{ textAlign: 'center', pt: 1 }}>
                      <Typography variant="body2" sx={{ fontFamily: FONT_FAMILY, color: '#64748B', fontSize: '0.85rem' }}>
                        Already registered?{' '}
                        <MuiLink
                          component="button"
                          type="button"
                          onClick={() => setShowSignUp(false)}
                          sx={{ fontFamily: FONT_FAMILY, color: '#E5232A', fontWeight: 700, textDecoration: 'none' }}
                        >
                          Sign In
                        </MuiLink>
                      </Typography>
                    </Box>
                  </Stack>
                </form>
              </Box>
            )}
          </Box>

          {/* Footer copyright */}
          <Box sx={{ textAlign: 'center', pt: 2, borderTop: '1px solid #F1F5F9' }}>
            <Typography variant="caption" sx={{ fontFamily: FONT_FAMILY, color: '#94A3B8', fontSize: '0.75rem' }}>
              © {new Date().getFullYear()} {settings?.CompanyName || settings?.appName || 'HIILLA'}. All rights reserved.
            </Typography>
          </Box>
        </Grid>

        {/* ── Right Operations Hero Visual Panel ── */}
        <Grid
          item
          xs={false}
          md={6}
          lg={7}
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'space-between',
            backgroundColor: '#0B1120',
            p: { md: 6, lg: 8 },
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Subtle glow circle */}
          <Box
            sx={{
              position: 'absolute',
              top: '-15%',
              right: '-15%',
              width: 500,
              height: 500,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(229,35,42,0.18) 0%, rgba(11,17,32,0) 70%)',
              pointerEvents: 'none'
            }}
          />

          {/* Top Operational Pill */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#10B981', boxShadow: '0 0 10px #10B981' }} />
            <Typography variant="caption" sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#94A3B8', letterSpacing: '0.5px' }}>
              HIILLA CLOUD PLATFORM OPERATIONAL
            </Typography>
          </Box>

          {/* Center Pitch */}
          <Box sx={{ maxWidth: 540 }}>
            <Typography
              variant="h3"
              sx={{
                fontFamily: FONT_FAMILY,
                fontWeight: 800,
                color: '#FFFFFF',
                letterSpacing: '-0.03em',
                lineHeight: 1.2,
                mb: 2.5
              }}
            >
              High-Speed On-Demand Dispatch & Fleet Management.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: FONT_FAMILY,
                color: '#94A3B8',
                fontSize: '1rem',
                lineHeight: 1.6,
                mb: 4
              }}
            >
              Log into the HIILLA Web Console to orchestrate automated rider dispatches, monitor live GPS delivery telemetry, and reconcile platform revenue in real-time.
            </Typography>

            {/* Feature Pills */}
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: '12px', backgroundColor: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '10px', backgroundColor: 'rgba(229, 35, 42, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#E5232A' }}>
                  <SpeedIcon />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#FFFFFF' }}>
                    Instant Fleet Telemetry
                  </Typography>
                  <Typography variant="caption" sx={{ fontFamily: FONT_FAMILY, color: '#94A3B8' }}>
                    Sub-second GPS updates on all registered and active riders.
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: '12px', backgroundColor: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '10px', backgroundColor: 'rgba(16, 185, 129, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10B981' }}>
                  <SecurityIcon />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#FFFFFF' }}>
                    Bank-Grade Settlements
                  </Typography>
                  <Typography variant="caption" sx={{ fontFamily: FONT_FAMILY, color: '#94A3B8' }}>
                    Integrated wallet adjustments, automated split payouts, and audit trails.
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Box>

          {/* Bottom Badge */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 3, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <Typography variant="caption" sx={{ fontFamily: FONT_FAMILY, color: '#64748B' }}>
              Enterprise High Availability Cluster
            </Typography>
            <Typography variant="caption" sx={{ fontFamily: FONT_FAMILY, color: '#64748B' }}>
              v3.4.0
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <AlertDialog open={commonAlert.open} onClose={handleCommonAlertClose}>
        {commonAlert.msg}
      </AlertDialog>
    </Box>
  );
}